import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import {
  buildGroupLabelForSchedule,
  groupIndexByCreationOrder,
  quotationScheduleGroupMetaFromQuotation,
} from '../../pages/SamplingSchedulesPage/samplingSchedulePlans';
import { dynamicRelation } from './utils';

function scheduleRelationFromTeam(team: DynamicEntityRow): DynamicEntityRow | null {
  const fromSchedule = dynamicRelation(team, 'sampling_schedule');
  if (fromSchedule) return fromSchedule as unknown as DynamicEntityRow;
  const workOrder = dynamicRelation(team, 'work_order');
  if (!workOrder) return null;
  const schedule = dynamicRelation(workOrder as unknown as DynamicEntityRow, 'sampling_schedule');
  return schedule ? (schedule as unknown as DynamicEntityRow) : null;
}

function groupDocIdFromSchedule(schedule: DynamicEntityRow | null): string {
  if (!schedule) return '';
  const fromSchedule = dynamicRelation(schedule, 'quotation_group');
  if (fromSchedule?.documentId) return String(fromSchedule.documentId).trim();
  const plans = schedule.sampling_plans;
  if (Array.isArray(plans)) {
    for (const plan of plans) {
      if (!plan || typeof plan !== 'object') continue;
      const fromPlan = dynamicRelation(plan as DynamicEntityRow, 'quotation_group');
      if (fromPlan?.documentId) return String(fromPlan.documentId).trim();
    }
  }
  return '';
}

/** Indeks group per quotation (urut createdAt, sama dengan modal quotation). */
export function buildQuotationGroupIndexMap(
  teams: DynamicEntityRow[],
): Map<string, Map<string, number>> {
  const groupsByQuotation = new Map<
    string,
    Array<{ documentId?: string | null; createdAt?: string | null }>
  >();

  for (const team of teams) {
    const schedule = scheduleRelationFromTeam(team);
    const quotation = schedule ? dynamicRelation(schedule, 'quotation') : null;
    const quotationDocId = String(quotation?.documentId ?? '').trim();
    if (!quotationDocId || !quotationScheduleGroupMetaFromQuotation(quotation)) continue;

    const group = schedule ? dynamicRelation(schedule, 'quotation_group') : null;
    const groupDocId = String(group?.documentId ?? groupDocIdFromSchedule(schedule)).trim();
    if (!groupDocId) continue;

    const list = groupsByQuotation.get(quotationDocId) ?? [];
    list.push({
      documentId: groupDocId,
      createdAt: group?.createdAt != null ? String(group.createdAt) : null,
    });
    groupsByQuotation.set(quotationDocId, list);
  }

  const out = new Map<string, Map<string, number>>();
  for (const [quotationDocId, groups] of groupsByQuotation) {
    out.set(quotationDocId, groupIndexByCreationOrder(groups));
  }
  return out;
}

export function groupLabelForTeamRow(
  team: DynamicEntityRow,
  groupIndexByQuotation: Map<string, Map<string, number>>,
): string | null {
  const schedule = scheduleRelationFromTeam(team);
  const quotation = schedule ? dynamicRelation(schedule, 'quotation') : null;
  const quotationDocId = String(quotation?.documentId ?? '').trim();
  if (!quotationDocId) return null;

  const groupDocId = groupDocIdFromSchedule(schedule);
  const groupIndex = groupIndexByQuotation.get(quotationDocId)?.get(groupDocId) ?? 0;
  return buildGroupLabelForSchedule(schedule, quotation, groupIndex);
}
